import { Component, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { AdminApi, ResourceApi } from "@app/shared/sdk";

@Component({
  selector: "accepted-links",
  templateUrl: "./accepted-links.component.html",
  styleUrls: ["./accepted-links.component.scss"],
})
export class AcceptedLinksComponent implements OnInit {
  isLoading = { action: "add", flag: false };
  linkForm: FormGroup;
  resource: any = null;
  initialLinks: string[] = [];

  constructor(
    private fb: FormBuilder,
    private adminApi: AdminApi,
    private resourceApi: ResourceApi
  ) {
    this.createForm();
  }

  ngOnInit() {
    this.getLinks();
  }

  get links() {
    return this.linkForm.get("links") as FormArray;
  }

  createForm() {
    this.linkForm = this.fb.group({
      links: this.fb.array([], Validators.compose([Validators.minLength(1), this.validateLinks]))
    });
  }

  async getLinks() {
    try {
      const response = await this.resourceApi
        .find({ where: { type: "links" } })
        .toPromise();
      if (response.length !== 0) {
        this.resource = response[0];
        this.initialLinks = [...this.resource.value];
        this.initializeLinks(this.resource.value);
      } else {
        // No links: do not add empty link, let user add via + button
        this.initialLinks = [];
        this.createForm();
      }
    } catch (error) {
      console.log("getLinks error....", error);
    }
  }

  validateLinks(control: FormArray): { [key: string]: any } | null {
    const urlPattern =
      /^(https:\/\/|\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w.-]*)*\/?$/;
    const linkValues = control.value.map((link) => link);
    const isDuplicate = linkValues.some(
      (value, index) => linkValues.indexOf(value) !== index
    );
    const invalidIndex = control.controls.findIndex(
      (link) => !urlPattern.test(link.value)
    );
    const duplicateIndex = linkValues.findIndex(
      (value, i) => linkValues.indexOf(value) !== i
    );
    const requiredIndex = control.controls.findIndex((link) => !link.value);

    if (requiredIndex !== -1) {
      control.controls[requiredIndex].setErrors({ required: true });
      return { required: true };
    }

    if (invalidIndex !== -1) {
      control.controls[invalidIndex].setErrors({ invalidLinks: true });
      return { invalidLinks: true };
    }

    if (control.controls.length !== 0 && duplicateIndex !== -1) {
      if (isDuplicate) {
        control.controls[duplicateIndex].setErrors({ duplicateLinks: true });
        return { duplicateLinks: true };
      } else {
        control.controls[duplicateIndex].setErrors(null); // Reset errors if no duplicates
      }
    }

    return null;
  }

  initializeLinks(links: string[]) {
    this.createForm();
    links.forEach((link) => {
      const linkControl = this.fb.control(link, [Validators.required]);
      this.links.push(linkControl);
    });
    // Do not add empty link at the end; user will add via + button
  }

  async validateBrokenLink(index: number) {
    this.isLoading.action = "add";
    this.isLoading.flag = true;
    const link = this.linkForm.getRawValue().links[index];
    try {
      await this.adminApi.checkLinkValidity(link, false).toPromise();
      this.linkForm.controls["links"]["controls"][index].setErrors(null);
    } catch (error) {
      // Handle the broken link error here
      this.linkForm.controls["links"]["controls"][index].setErrors({
        brokenLink: true,
      });
    } finally {
      this.isLoading.flag = false;
    }
  }

  async addLink(index?: number) {
    // Only validate previous link if index is provided and not empty
    if (
      index !== undefined &&
      this.linkForm.getRawValue().links[index] !== ""
    ) {
      await this.validateBrokenLink(index);
    }
    const linkControl = this.fb.control("", [Validators.required]);
    this.links.push(linkControl);
  }

  removeLink(index: number) {
    if (this.links.length > 1) {
      this.links.removeAt(index);
    }
  }

  getErrors(link) {
    console.log(link);
  }

  hasFormChanged(): boolean {
    const currentLinks = this.linkForm.getRawValue().links;
    if (currentLinks.length !== this.initialLinks.length) return true;
    for (let i = 0; i < currentLinks.length; i++) {
      if (currentLinks[i] !== this.initialLinks[i]) return true;
    }
    return false;
  }

  async saveLinks() {
    if (!this.hasFormChanged()) return;
    let obj: any = {
      key: "acceptedLinks",
      value: this.linkForm.getRawValue().links,
      type: "links",
      colorMix: null,
      languageId: null,
    };
    if (this.resource && this.resource.id) {
      obj.id = this.resource.id;
    }
    this.isLoading.action = "save";
    this.isLoading.flag = true;
    try {
      await this.resourceApi.patchOrCreate(obj).toPromise();
      await this.getLinks();
    } catch (error) {
      console.log(error);
    } finally {
      this.isLoading.action = "add";
      this.isLoading.flag = false;
    }
  }
}
